import React from 'react';
import styled from 'styled-components';
import {FeaturesIconEnum} from '../icons/features/featuresIcon.types';
import {FeaturesIcon} from '../icons/features/FeaturesIcon';
import {Hyperlink, Typography as Text, Typography, VerticalSpacer} from '..';
import {AccentColorOptions} from 'epic-ue-theme';
import {AnimateOnViewWrap} from '../../wraps';
import Grid from '@material-ui/core/Grid';
import {Button} from '../buttons';
import {CmsButtonProps} from '../buttons';
import {Eyebrow, EyebrowProps} from '../eyebrow';

const Styled = styled.div<{accentColor: AccentColorOptions}>`
    && svg {
        width: 70px;
        font-size: 7.3em;
        ${(p) => p.theme.breakpoints.down('sm')} {
            font-size: 5.7em;
        }
    }

    .eyebrow-wrapper .title {
        font-size: 16px;
    }

    .text-card-link {
        white-space: normal;
        display: contents;
    }

    .ue-rich-text ul {
        list-style-type: none;

        li:before {
            content: '•';
            color: ${(p) => (p.accentColor ? p.theme.palette.accent[p.accentColor] : 'initial')};
            font-weight: bold;
            display: inline-block;
            width: 1em;
            margin-left: -1em;
        }
    }
`;

export interface TextCardProps {
    eyebrow?: EyebrowProps;
    title?: string;
    description?: string;
    accentColor?: AccentColorOptions;
    button?: CmsButtonProps;
    links?: CmsButtonProps[];
    svgIcon?: FeaturesIconEnum;
    disableAnim?: boolean;
}

export const TextCard = (props: TextCardProps): JSX.Element => {
    const {
        eyebrow = {},
        title,
        accentColor = 'sky',
        description,
        button = {},
        svgIcon,
        links = [],
        disableAnim
    } = props;

    const contentElem = (
        <Grid item sm={12}>
            {svgIcon && (
                <>
                    <FeaturesIcon variant={svgIcon} accentColor={accentColor} />
                    <VerticalSpacer variant="custom" spacing={5} downSmSpacing={4} />
                </>
            )}
            <AnimateOnViewWrap translateY={60} disable={disableAnim}>
                {eyebrow && eyebrow.text && (
                    <>
                        <VerticalSpacer variant="custom" spacing={3} downSmSpacing={2} />
                        <Eyebrow {...eyebrow} as="span" />
                        <VerticalSpacer variant="custom" spacing={2} downSmSpacing={1} />
                    </>
                )}
                {title && (
                    <>
                        <Typography variant="h3" big={!eyebrow?.text}>
                            {title}
                        </Typography>
                    </>
                )}
                {description && (
                    <>
                        <VerticalSpacer variant="eyebrow" />
                        <div className="description">
                            <Text variant="p">{description}</Text>
                        </div>
                    </>
                )}
                {button && button.label && (
                    <>
                        <VerticalSpacer variant="custom" spacing={4} />
                        <Button type={button.type} href={button.href} color={accentColor}>
                            {button.label}
                        </Button>
                    </>
                )}
                {links && links.length ? (
                    <>
                        <VerticalSpacer variant="custom" spacing={1} />
                        {links.map((link, i) => (
                            <React.Fragment key={link.label}>
                                <Hyperlink href={link.href} accentColor={accentColor}>
                                    <Typography
                                        variant="p"
                                        caption={true}
                                        className="text-card-link">
                                        {link.label}
                                    </Typography>
                                </Hyperlink>
                                {i < links.length - 1 ? <VerticalSpacer variant="paragraph" /> : ''}
                            </React.Fragment>
                        ))}
                    </>
                ) : (
                    ''
                )}
            </AnimateOnViewWrap>
        </Grid>
    );

    return (
        <Styled accentColor={accentColor} className="text-card">
            {contentElem}
        </Styled>
    );
};
